import React, { useEffect, useState } from "react";
import { ArrowLeft, MapPin, Phone, Mail, Clock, Headset, Loader2, CheckCircle2, Send } from "lucide-react";
import { SocialLinks } from "../components/SocialLinks";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TITLE = "Contact Paras CashBack | Support, Merchant & Partnership Enquiries";
const DESC = "Get in touch with Paras CashBack for customer support, merchant assistance, partnership enquiries and general questions.";

const empty = { name: "", mobile: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const prev = document.title; document.title = TITLE;
    const m = document.querySelector('meta[name="description"]');
    const pd = m ? m.getAttribute("content") : null;
    if (m) m.setAttribute("content", DESC);
    fetch(`${BACKEND}/api/public/site-settings`).then((r) => r.json()).then(setS).catch(() => {});
    return () => { document.title = prev; if (m && pd) m.setAttribute("content", pd); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.subject.trim() || !form.message.trim()) {
      setErr("Please fill in Name, Mobile Number, Subject and Message.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not send message");
      setDone(true); setForm(empty);
    } catch (e) { setErr(e.message || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="contact-page">
      <div className="mx-auto max-w-5xl px-5 py-10">
        <a href="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400" data-testid="contact-back">
          <ArrowLeft className="h-4 w-4" /> Back to Paras CashBack
        </a>

        <h1 className="font-heading text-3xl font-extrabold sm:text-4xl">Contact Us</h1>
        {s?.company_name && <p className="mt-2 text-slate-400" data-testid="contact-company">{s.company_name}</p>}
        {s?.contact_intro && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300">{s.contact_intro}</p>}

        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {/* LEFT: details */}
          <div className="space-y-4">
            <InfoCard icon={MapPin} label="Address" testid="contact-address">
              <span className="whitespace-pre-line">{s?.address || "—"}</span>
            </InfoCard>
            <InfoCard icon={Phone} label="Phone" testid="contact-phone">
              {s?.phone ? <a href={`tel:${s.phone.replace(/\s/g, "")}`} className="hover:text-emerald-400">{s.phone}</a> : "—"}
            </InfoCard>
            <InfoCard icon={Mail} label="Email" testid="contact-email">
              {s?.email ? <a href={`mailto:${s.email}`} className="hover:text-emerald-400">{s.email}</a> : "—"}
            </InfoCard>
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoCard icon={Clock} label="Working Hours" testid="contact-working-hours">{s?.working_hours || "—"}</InfoCard>
              <InfoCard icon={Headset} label="Support Hours" testid="contact-support-hours">{s?.support_hours || "—"}</InfoCard>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Follow us</p>
              <div className="mt-3"><SocialLinks social={s?.social} /></div>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <h2 className="font-heading text-lg font-bold">Send Us a Message</h2>
            {done ? (
              <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center" data-testid="contact-success">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                <p className="mt-3 font-semibold text-emerald-300">Message sent</p>
                <p className="mt-1 text-sm text-slate-300">Thank you! Our team will get back to you during support hours.</p>
                <button onClick={() => setDone(false)} className="mt-4 rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:bg-white/5" data-testid="contact-send-another">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Name *</label>
                  <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Mobile Number *</label>
                    <input data-testid="contact-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Email Address</label>
                    <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Subject *</label>
                  <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Message *</label>
                  <textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </div>
                {err && <p className="text-sm text-rose-400" data-testid="contact-error">{err}</p>}
                <button data-testid="contact-submit" type="submit" disabled={busy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-100"><Icon className="h-4 w-4 text-emerald-400" /><p className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</p></div>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">{children}</p>
    </div>
  );
}
